/**
 * Equipment Tracker - Dashboard
 * @author Omid Zaeri (zaerio)
 */

import React, { useEffect, useState } from 'react';
import {
  Table, Header, SpaceBetween, Button, StatusIndicator, Box, Badge,
} from '@cloudscape-design/components';
import { getEquipment, deleteEquipment, getTeamEquipment } from '../services/api';
import { useAuth } from '../context/AuthContext';

function StatCard({ value, label, accent, icon }) {
  return (
    <div className="stat-card" style={{ display: 'flex', alignItems: 'center' }}>
      <div className="stat-card__accent" style={{ background: accent }} />
      <div>
        <div className="stat-card__value">{icon} {value}</div>
        <div className="stat-card__label">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { isAdmin, isRegionalManager, isManager, isImpersonating, user } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [teamEquipment, setTeamEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamLoading, setTeamLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const loadEquipment = () => {
    setLoading(true);
    // When impersonating, fetch only that user's equipment
    const viewAs = isImpersonating ? user?.alias : undefined;
    getEquipment(viewAs)
      .then(setEquipment)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEquipment(); }, [isImpersonating, user?.alias]);

  useEffect(() => {
    if (isManager) {
      setTeamLoading(true);
      const viewAs = isImpersonating ? user?.alias : undefined;
      getTeamEquipment(viewAs)
        .then(data => setTeamEquipment(data.equipment || []))
        .catch(console.error)
        .finally(() => setTeamLoading(false));
    }
  }, [isManager, isImpersonating, user?.alias]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.type}" (${item.serialNumber || item.equipmentId}) assigned to ${item.assignedTo}?`)) return;
    setDeleting(item.equipmentId);
    try {
      await deleteEquipment(item.equipmentId);
      loadEquipment();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const statusIndicator = (item) => {
    if (item.status === 'pending-aup') {
      return <StatusIndicator type="warning">Pending AUP</StatusIndicator>;
    }
    if (item.lastReportedStatus) {
      const reportMap = {
        'Working': <StatusIndicator type="success">Active</StatusIndicator>,
        'Needs Repair': <StatusIndicator type="warning">Needs Repair</StatusIndicator>,
        'Lost': <StatusIndicator type="error">Lost</StatusIndicator>,
        'Stolen': <StatusIndicator type="error">Stolen</StatusIndicator>,
        'Decommissioned': <StatusIndicator type="stopped">Decommissioned</StatusIndicator>,
      };
      return reportMap[item.lastReportedStatus] || <StatusIndicator type="info">{item.lastReportedStatus}</StatusIndicator>;
    }
    if (item.status === 'active') {
      return <StatusIndicator type="success">Active</StatusIndicator>;
    }
    return <StatusIndicator type="info">{item.status}</StatusIndicator>;
  };

  const tierBadge = (tier) => {
    const colors = { Manager: 'blue', Engineer: 'green', Technician: 'grey', EC: 'red' };
    return <Badge color={colors[tier] || 'grey'}>{tier}</Badge>;
  };

  const renderNextReportDue = (dateStr) => {
    if (!dateStr) return <span style={{ color: '#8d8d8d' }}>—</span>;
    const due = new Date(dateStr);
    const now = new Date();
    const daysUntil = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    const formatted = due.toLocaleDateString();

    if (daysUntil < 0) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#da1e28', fontWeight: 600 }}>
          <span>⚠</span> {formatted}
        </span>
      );
    }
    if (daysUntil <= 14) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b45309', fontWeight: 600 }}>
          <span>⏰</span> {formatted}
        </span>
      );
    }
    return <span style={{ color: '#525252' }}>{formatted}</span>;
  };

  // Compute stats
  const total = equipment.length;
  const active = equipment.filter(e => e.status === 'active' || e.lastReportedStatus === 'Working').length;
  const pendingAup = equipment.filter(e => e.status === 'pending-aup').length;
  const overdue = equipment.filter(e => {
    if (!e.nextReminderDate) return false;
    return new Date(e.nextReminderDate) < new Date();
  }).length;

  return (
    <SpaceBetween size="l">
      {/* Stat Cards */}
      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <StatCard value={total} label="Total Equipment" accent="#0f62fe" />
          <StatCard value={active} label="Active" accent="#198038" />
          <StatCard value={pendingAup} label="Pending AUP" accent="#f1c21b" />
          <StatCard value={overdue} label="Reports Overdue" accent="#da1e28" />
        </div>
      )}

      {/* My Team section — Managers and Regional Managers */}
      {isManager && (
        <Table
          loading={teamLoading}
          items={teamEquipment}
          header={<Header variant="h2">My Team's Equipment</Header>}
          columnDefinitions={[
            { id: 'assignedTo', header: 'Assigned To', cell: (e) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e8f0fe', color: '#0f62fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                  {e.assignedTo?.[0]?.toUpperCase()}
                </span>
                {e.assignedTo}
              </span>
            )},
            { id: 'type', header: 'Equipment', cell: (e) => <span style={{ fontWeight: 600 }}>{e.type}</span> },
            { id: 'serialNumber', header: 'Serial #', cell: (e) => e.serialNumber ? <code style={{ fontSize: '0.82rem', background: '#f4f4f4', padding: '2px 6px', borderRadius: 4 }}>{e.serialNumber}</code> : <span style={{ color: '#8d8d8d' }}>—</span> },
            { id: 'status', header: 'Status', cell: (e) => statusIndicator(e) },
            { id: 'policyAccepted', header: 'AUP', cell: (e) => e.policyAccepted ? <Badge color="green">Accepted</Badge> : <Badge color="grey">Pending</Badge> },
            { id: 'nextReminder', header: 'Report Due', cell: (e) => renderNextReportDue(e.nextReminderDate) },
            { id: 'actions', header: '', cell: (e) => <Button href={`/equipment/${e.equipmentId}`} variant="link">View →</Button> },
          ]}
          empty={<Box textAlign="center" padding="l"><b>No equipment found for your direct reports</b></Box>}
        />
      )}

      <Table
        loading={loading}
        items={equipment}
        header={
          <Header
            variant="h1"
            actions={
              isAdmin && (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button href="/equipment/new" variant="primary">+ Assign Equipment</Button>
                  <Button href="/users" variant="normal">Manage Users</Button>
                </SpaceBetween>
              )
            }
          >
            Equipment
          </Header>
        }
        columnDefinitions={[
          {
            id: 'type',
            header: 'Equipment',
            cell: (e) => (
              <span style={{ fontWeight: 600, color: '#161616' }}>{e.type}</span>
            ),
          },
          { id: 'serialNumber', header: 'Serial #', cell: (e) => e.serialNumber ? <code style={{ fontSize: '0.82rem', background: '#f4f4f4', padding: '2px 6px', borderRadius: 4 }}>{e.serialNumber}</code> : <span style={{ color: '#8d8d8d' }}>—</span> },
          ...(isAdmin ? [{ id: 'assignedTo', header: 'Assigned To', cell: (e) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e8f0fe', color: '#0f62fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                {e.assignedTo?.[0]?.toUpperCase()}
              </span>
              {e.assignedTo}
            </span>
          )}] : []),
          { id: 'status', header: 'Status', cell: (e) => statusIndicator(e) },
          { id: 'policyAccepted', header: 'AUP', cell: (e) => e.policyAccepted
            ? <Badge color="green">Accepted</Badge>
            : <Badge color="grey">Pending</Badge>
          },
          { id: 'assignedDate', header: 'Assigned', cell: (e) => new Date(e.assignedDate).toLocaleDateString() },
          { id: 'nextReminder', header: 'Report Due', cell: (e) => renderNextReportDue(e.nextReminderDate) },
          {
            id: 'actions',
            header: '',
            cell: (e) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button href={`/equipment/${e.equipmentId}`} variant="link">View →</Button>
                {isAdmin && (
                  <Button variant="link" onClick={() => handleDelete(e)} loading={deleting === e.equipmentId}>
                    Remove
                  </Button>
                )}
              </SpaceBetween>
            ),
          },
        ]}
        empty={
          <Box textAlign="center" padding="xxl">
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📦</div>
            <Box variant="h3" padding={{ bottom: 'xs' }}>No equipment found</Box>
            <Box color="text-body-secondary">
              {isAdmin ? 'Start by assigning equipment to team members.' : 'No equipment assigned to you yet.'}
            </Box>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
